/**
 * IndexedDB-backed persistence for Pulsewright projects.
 *
 * Two stores:
 *   - "snapshots" — user-named saved versions. Keyed by `name`.
 *   - "autosave"  — single rolling auto-save. Keyed by `_autosave`.
 *
 * Everything is wrapped in a versioned envelope so we can migrate the
 * schema later (e.g. when DemoSong becomes the full Project format).
 */

import { openDB, type IDBPDatabase } from 'idb';
import type { DemoSong } from '../audio/demo-song';
import type { PatternPreset } from './pattern-presets';

export const DB_NAME = 'pulsewright';
export const DB_VERSION = 2;
export const SNAPSHOT_STORE = 'snapshots';
export const AUTOSAVE_STORE = 'autosave';
export const PRESET_STORE = 'patternPresets';
export const AUTOSAVE_KEY = '_autosave';

export const FORMAT_TAG = 'pulsewright-demo-v1';

export interface SavedSnapshot {
  /** Snapshot name (used as the IDB key for `snapshots`). */
  name: string;
  /** ISO 8601 timestamp. */
  savedAt: string;
  /** Format tag — bump when the song schema changes. */
  format: string;
  song: DemoSong;
}

export interface SnapshotMeta {
  name: string;
  savedAt: string;
}

/**
 * Open (or create) the database. Idempotent — multiple calls share the same
 * connection.
 */
let dbPromise: Promise<IDBPDatabase> | null = null;
export function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) {
            db.createObjectStore(SNAPSHOT_STORE, { keyPath: 'name' });
          }
          if (!db.objectStoreNames.contains(AUTOSAVE_STORE)) {
            db.createObjectStore(AUTOSAVE_STORE);
          }
        }
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains(PRESET_STORE)) {
            db.createObjectStore(PRESET_STORE, { keyPath: 'name' });
          }
        }
      },
    });
  }
  return dbPromise;
}

/** Reset the shared connection. Tests use this between cases. */
export async function resetDBForTests(): Promise<void> {
  if (dbPromise) {
    try {
      const db = await dbPromise;
      db.close();
    } catch {
      /* ignore */
    }
  }
  dbPromise = null;
}

/** Clear every snapshot + autosave + presets. Useful for tests. */
export async function clearAllForTests(): Promise<void> {
  const db = await getDB();
  await db.clear(SNAPSHOT_STORE);
  await db.clear(AUTOSAVE_STORE);
  await db.clear(PRESET_STORE);
}

/* ─── Pattern preset persistence ───────────────────────────────────── */

/** Save (or overwrite) a user-authored pattern preset. */
export async function savePreset(preset: PatternPreset): Promise<PatternPreset> {
  if (!preset.name?.trim()) throw new Error('Preset name must be non-empty.');
  const stored: PatternPreset = {
    ...preset,
    name: preset.name.trim(),
    source: 'user',
    savedAt: new Date().toISOString(),
  };
  const db = await getDB();
  await db.put(PRESET_STORE, stored);
  return stored;
}

/** Load a named user preset, or null. */
export async function loadPreset(name: string): Promise<PatternPreset | null> {
  const db = await getDB();
  const result = (await db.get(PRESET_STORE, name)) as PatternPreset | undefined;
  return result ?? null;
}

/** List all user presets, newest first. */
export async function listPresets(): Promise<PatternPreset[]> {
  const db = await getDB();
  const all = (await db.getAll(PRESET_STORE)) as PatternPreset[];
  return all.sort((a, b) => {
    const ta = a.savedAt ?? '';
    const tb = b.savedAt ?? '';
    return ta > tb ? -1 : ta < tb ? 1 : 0;
  });
}

/** Delete a named user preset. No-op if missing. */
export async function deletePreset(name: string): Promise<void> {
  const db = await getDB();
  await db.delete(PRESET_STORE, name);
}

/** Wrap a song in the versioned envelope. */
export function makeSnapshot(name: string, song: DemoSong, now: Date = new Date()): SavedSnapshot {
  if (!name || !name.trim()) throw new Error('Snapshot name must be non-empty.');
  return {
    name: name.trim(),
    savedAt: now.toISOString(),
    format: FORMAT_TAG,
    song,
  };
}

/**
 * Save / overwrite a named snapshot.
 */
export async function saveSnapshot(name: string, song: DemoSong): Promise<SavedSnapshot> {
  const snap = makeSnapshot(name, song);
  const db = await getDB();
  await db.put(SNAPSHOT_STORE, snap);
  return snap;
}

/** Load a named snapshot, or null if missing. */
export async function loadSnapshot(name: string): Promise<SavedSnapshot | null> {
  const db = await getDB();
  const result = (await db.get(SNAPSHOT_STORE, name)) as SavedSnapshot | undefined;
  return result ?? null;
}

/** List all saved snapshots, newest first. */
export async function listSnapshots(): Promise<SnapshotMeta[]> {
  const db = await getDB();
  const all = (await db.getAll(SNAPSHOT_STORE)) as SavedSnapshot[];
  return all
    .map((s) => ({ name: s.name, savedAt: s.savedAt }))
    .sort((a, b) => (a.savedAt > b.savedAt ? -1 : a.savedAt < b.savedAt ? 1 : 0));
}

/** Delete a named snapshot. No-op if missing. */
export async function deleteSnapshot(name: string): Promise<void> {
  const db = await getDB();
  await db.delete(SNAPSHOT_STORE, name);
}

/** Save the rolling auto-save. */
export async function saveAutosave(song: DemoSong): Promise<void> {
  const snap = makeSnapshot('_autosave', song);
  const db = await getDB();
  await db.put(AUTOSAVE_STORE, snap, AUTOSAVE_KEY);
}

/** Load the rolling auto-save, or null. */
export async function loadAutosave(): Promise<SavedSnapshot | null> {
  const db = await getDB();
  const result = (await db.get(AUTOSAVE_STORE, AUTOSAVE_KEY)) as SavedSnapshot | undefined;
  return result ?? null;
}

/** Clear the rolling auto-save (e.g. after the user explicitly opens a snapshot). */
export async function clearAutosave(): Promise<void> {
  const db = await getDB();
  await db.delete(AUTOSAVE_STORE, AUTOSAVE_KEY);
}

/**
 * Convert a snapshot to a JSON string suitable for download as a .pwt file.
 * Pretty-printed for human readability.
 */
export function serializeForExport(snap: SavedSnapshot): string {
  return JSON.stringify(snap, null, 2);
}

/**
 * Parse a JSON string from an imported .pwt file. Throws on missing or
 * unrecognized format. (Migration code from older formats will live here.)
 */
export function parseFromImport(text: string): SavedSnapshot {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (err) {
    throw new Error(`Invalid JSON in import: ${err instanceof Error ? err.message : String(err)}`);
  }
  if (!raw || typeof raw !== 'object') {
    throw new Error('Import file is not a Pulsewright project (expected a JSON object).');
  }
  const obj = raw as Partial<SavedSnapshot>;
  if (obj.format !== FORMAT_TAG) {
    throw new Error(
      `Unsupported file format: "${obj.format}". This build expects "${FORMAT_TAG}".`,
    );
  }
  if (!obj.song || typeof obj.song !== 'object') {
    throw new Error('Import file is missing the `song` field.');
  }
  if (!obj.name || typeof obj.name !== 'string') {
    throw new Error('Import file is missing the `name` field.');
  }
  if (!obj.savedAt || typeof obj.savedAt !== 'string') {
    throw new Error('Import file is missing the `savedAt` field.');
  }
  return obj as SavedSnapshot;
}
